'use client';

import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { useToastActions } from '../ui/Toast';
import { workflowApi } from '../../services/api';
import { Download, Upload, X, FileZip, Check, Warning } from '@phosphor-icons/react';

// ── Export Dialog ────────────────────────────────────────────────────────────
interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName: string;
}

export function ExportDialog({ isOpen, onClose, workflowId, workflowName }: ExportDialogProps) {
  const { t } = useTranslation();
  const toast = useToastActions();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { blob, filename } = await workflowApi.exportWorkflow(workflowId);
      
      // 创建下载链接
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${workflowName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(t('workflow.exportSuccess'));
      onClose();
    } catch (error) {
      console.error('Export workflow error:', error);
      toast.error(error instanceof Error ? error.message : t('workflow.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Download className="w-5 h-5 text-emerald-600" weight="fill" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">{t('workflow.exportWorkflow')}</h3>
                <p className="text-xs text-zinc-500">{t('workflow.exportWorkflowDesc')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-700">{t('workflow.exportFormat')}</label>
              <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl border border-zinc-200">
                <FileZip className="w-8 h-8 text-emerald-500" weight="fill" />
                <div>
                  <p className="text-sm font-medium text-zinc-900">{t('workflow.exportFormatZip')}</p>
                  <p className="text-xs text-zinc-500">.zip</p>
                </div>
                <Check className="w-5 h-5 text-emerald-500 ml-auto" weight="bold" />
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-zinc-50/50 border-t border-zinc-100 flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={isExporting}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              isLoading={isExporting}
              leftIcon={<Download className="w-4 h-4" />}
            >
              {t('workflow.export.label')}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Import Dialog ────────────────────────────────────────────────────────────
interface ImportResult {
  id: string;
  name: string;
  description?: string;
  status?: string;
  nodes?: unknown[];
  edges?: unknown[];
  needsReconfigure?: boolean;
}

interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: (workflow: { id: string; name: string }) => void;
}

export function ImportDialog({ isOpen, onClose, onImportSuccess }: ImportDialogProps) {
  const { t } = useTranslation();
  const toast = useToastActions();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      validateAndSetFile(files[0]);
    }
  }, []);

  const validateAndSetFile = (file: File) => {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.zip') && !fileName.endsWith('.bgl')) {
      toast.error(t('workflow.invalidFileFormat'));
      return;
    }
    
    // 检查文件大小 (最大 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('workflow.fileTooLarge'));
      return;
    }
    
    setSelectedFile(file);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error(t('workflow.import.noFile'));
      return;
    }

    setIsImporting(true);
    try {
      const result = await workflowApi.importWorkflow(selectedFile) as ImportResult;
      
      if (result.needsReconfigure) {
        toast.warning(t('workflow.importSuccessWithReconfigure'));
      } else {
        toast.success(t('workflow.importSuccess'));
      }
      
      onImportSuccess({
        id: result.id,
        name: result.name,
      });
      
      // 重置状态
      setSelectedFile(null);
      onClose();
    } catch (error) {
      console.error('Import workflow error:', error);
      toast.error(error instanceof Error ? error.message : t('workflow.importFailed'));
    } finally {
      setIsImporting(false);
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                <Upload className="w-5 h-5 text-blue-600" weight="fill" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">{t('workflow.importWorkflow')}</h3>
                <p className="text-xs text-zinc-500">{t('workflow.importWorkflowDesc')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* 文件上传区域 */}
            <div
              onClick={handleClickUpload}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                isDragging
                  ? 'border-blue-500 bg-blue-50'
                  : selectedFile
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip,.bgl"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {selectedFile ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <FileZip className="w-6 h-6 text-emerald-600" weight="fill" />
                  </div>
                  <p className="text-sm font-medium text-zinc-900">{selectedFile.name}</p>
                  <p className="text-xs text-zinc-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    className="mt-2 text-xs text-red-500 hover:text-red-600"
                  >
                    {t('common.remove')}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center">
                    <Upload className="w-6 h-6 text-zinc-400" weight="fill" />
                  </div>
                  <p className="text-sm font-medium text-zinc-700">{t('workflow.importDragHint')}</p>
                  <p className="text-xs text-zinc-500">{t('workflow.importFormatHint')}</p>
                </div>
              )}
            </div>

            {/* 提示信息 */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Warning className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" weight="fill" />
              <p className="text-xs text-amber-700">
                {t('workflow.needsReconfigureHint')}
                <br />
                {t('workflow.import.importedFrom')} API Key, Access Token, Webhook Secret
              </p>
            </div>
          </div>

          <div className="px-6 py-4 bg-zinc-50/50 border-t border-zinc-100 flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={isImporting}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleImport}
              isLoading={isImporting}
              disabled={!selectedFile}
              leftIcon={<Upload className="w-4 h-4" />}
            >
              {t('workflow.import.label')}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
